import { ContractBundle } from "@/pages/contracts/hooks";
import { ContractRevenueItem } from "@/pages/contracts/types";

export type TableBlock =
  | { kind: "item"; item: ContractRevenueItem }
  | { kind: "bundle"; bundle: ContractBundle; lines: ContractRevenueItem[] };

export type BundleBlocks<Item, Bundle> = (
  | { kind: "item"; item: Item }
  | { kind: "bundle"; bundle: Bundle; lines: Item[] }
)[];

// Groups lines, in table order, under their bundles. A bundle sits where its
// first line is; a line whose bundle isn't known (not loaded yet) shows on its
// own. Shared by the live table and a terms version, whose bundles are keyed
// differently.
export const groupByBundle = <
  Item extends { contractBundleId?: string | null },
  Bundle
>(
  orderedItems: Item[],
  bundles: Bundle[],
  bundleId: (bundle: Bundle) => string | null | undefined
): BundleBlocks<Item, Bundle> => {
  const bundlesById = new Map(
    bundles.map((bundle) => [bundleId(bundle), bundle])
  );
  const blocks: BundleBlocks<Item, Bundle> = [];
  const linesById = new Map<string, Item[]>();

  orderedItems.forEach((item) => {
    const id = item.contractBundleId;
    const bundle = id ? bundlesById.get(id) : undefined;
    if (!id || !bundle) {
      blocks.push({ kind: "item", item });
      return;
    }
    const lines = linesById.get(id);
    if (lines) {
      lines.push(item);
      return;
    }
    const created = [item];
    linesById.set(id, created);
    blocks.push({ kind: "bundle", bundle, lines: created });
  });

  return blocks;
};

export const groupBundleBlocks = (
  orderedItems: ContractRevenueItem[],
  bundles: ContractBundle[]
): TableBlock[] => groupByBundle(orderedItems, bundles, ({ id }) => id);

// A bundle's total is the sum of its lines; unknown when any line's is.
export const bundleTotals = (lines: ContractRevenueItem[]) => {
  const sum = (field: "total" | "totalLocal") =>
    lines.some((line) => line[field] == null)
      ? null
      : lines.reduce((total, line) => total + (line[field] ?? 0), 0);
  return { total: sum("total"), totalLocal: sum("totalLocal") };
};

const move = <T>(list: T[], from: number, to: number) => {
  const next = [...list];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

// A standalone line or a whole bundle moved to another place in the table.
export const moveBlock = (blocks: TableBlock[], from: number, to: number) =>
  move(blocks, from, to);

// A bundle's line moved among its own lines; it never leaves the bundle.
export const moveBundleLine = (
  blocks: TableBlock[],
  bundleId: string,
  from: number,
  to: number
): TableBlock[] =>
  blocks.map((block) =>
    block.kind === "bundle" && block.bundle.id === bundleId
      ? { ...block, lines: move(block.lines, from, to) }
      : block
  );

// The lines in table order, numbered as the order the server keeps; each
// bundle's lines stay together, as the server requires.
export const linesInOrder = (blocks: TableBlock[]): ContractRevenueItem[] =>
  blocks
    .flatMap((block) => (block.kind === "item" ? [block.item] : block.lines))
    .map((item, index) => ({ ...item, itemOrder: index }));
